import React, { useEffect, useRef, useState } from "react";

import type { ImageProps as UnpicImageProps } from "@unpic/react";
import { Image as UnpicImage } from "@unpic/react";

import { createImageUrl } from "~/util/urls";

/**
 * An image wrapper to allow quick hand off to the Image component.
 *
 * It is recommended to use this component over the Next Image component directly.
 *
 * @info: https://unpic.pics/img/react/#cdn
 */

const Image: React.FC<UnpicImageProps> = (props) => {
  const imageRef = useRef<HTMLImageElement>(null);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [isCached, setIsCached] = useState<boolean>(false);

  useEffect(() => {
    if (imageRef.current) {
      if (imageRef.current.complete) {
        setIsCached(true);
        setIsLoaded(true);
      }
    }
  }, []);

  const handleImageLoad = () => {
    setIsLoaded(true);
  };

  const loadInClasses = isCached
    ? ""
    : `duration-300 transition-opacity ${isLoaded ? "opacity-100" : "opacity-0"}`;
  const finalClassName = `${loadInClasses} ${props.className || ""}`.trim();

  return (
    <UnpicImage
      {...props}
      ref={imageRef}
      src={props.src ? createImageUrl(props.src) : ""}
      onLoad={handleImageLoad}
      className={finalClassName}
    />
  );
};

interface BasicImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  loading?: "lazy" | "eager";
}

export const BasicImage: React.FC<BasicImageProps> = ({
  loading = "lazy",
  ...props
}) => {
  const [isLoaded, setIsLoaded] = useState<boolean>(false);

  return (
    <img
      {...props}
      onLoad={() => setTimeout(() => setIsLoaded(true), 10)}
      className={`opacity-0 transition-opacity duration-300 ${isLoaded ? "opacity-100" : ""} ${props?.className || ""}`}
      loading={loading}
    />
  );
};

export default Image;
